import React, { createContext, useContext, useMemo } from 'react';
import type { BookingFormBrand, BrandTheme } from './types';

/**
 * Brand theme configurations
 * 
 * camp67: Coral (red-200) CTA, Amber/orange accents
 * kumbh-sukrit: Emerald green CTA and accents
 */
const brandThemes: Record<BookingFormBrand, BrandTheme> = {
  'camp67': {
    // CTA - coral/salmon red
    ctaBg: 'bg-red-200',
    ctaHover: 'hover:bg-red-300',
    ctaShadow: 'shadow-red-400',
    ctaRing: 'focus:ring-red-300',
    // Accents - amber/orange (V3 style)
    accentBg: 'bg-amber-500',
    accentBgLight: 'bg-amber-100',
    accentBorder: 'border-amber-400',
    accentText: 'text-amber-600',
    accentBadge: 'bg-amber-500',
    // Calendar-specific colors (Tailwind classes for static usage)
    calendarSelected: 'bg-amber-500',
    calendarSelectedHover: 'hover:bg-amber-500',
    calendarRangeBg: 'bg-amber-50',
    calendarRangeText: 'text-amber-900',
    calendarBadgeBg: 'bg-amber-50',
    calendarBadgeText: 'text-amber-700',
    // Calendar hex colors for CSS variables (runtime dynamic)
    calendarSelectedHex: '#f59e0b', // amber-500
    calendarRangeBgHex: '#fffbeb', // amber-50
    calendarRangeTextHex: '#78350f', // amber-900
    calendarBadgeBgHex: '#fffbeb', // amber-50
    calendarBadgeTextHex: '#b45309', // amber-700
  },
  'kumbh-sukrit': {
    // CTA - emerald green
    ctaBg: 'bg-emerald-600',
    ctaHover: 'hover:bg-emerald-700',
    ctaShadow: 'shadow-emerald-400',
    ctaRing: 'focus:ring-emerald-500',
    // Accents - emerald green shades
    accentBg: 'bg-emerald-500',
    accentBgLight: 'bg-emerald-100',
    accentBorder: 'border-emerald-400',
    accentText: 'text-emerald-600',
    accentBadge: 'bg-emerald-500',
    // Calendar-specific colors (Tailwind classes for static usage)
    calendarSelected: 'bg-emerald-600',
    calendarSelectedHover: 'hover:bg-emerald-600',
    calendarRangeBg: 'bg-emerald-50',
    calendarRangeText: 'text-emerald-900',
    calendarBadgeBg: 'bg-emerald-50',
    calendarBadgeText: 'text-emerald-700',
    // Calendar hex colors for CSS variables (runtime dynamic)
    calendarSelectedHex: '#059669', // emerald-600
    calendarRangeBgHex: '#ecfdf5', // emerald-50
    calendarRangeTextHex: '#064e3b', // emerald-900
    calendarBadgeBgHex: '#ecfdf5', // emerald-50
    calendarBadgeTextHex: '#047857', // emerald-700
  },
};

interface BrandThemeContextValue {
  brand: BookingFormBrand;
  theme: BrandTheme;
}

const BrandThemeContext = createContext<BrandThemeContextValue>({
  brand: 'camp67',
  theme: brandThemes['camp67'],
});

interface BrandThemeProviderProps {
  brand: BookingFormBrand;
  children: React.ReactNode;
}

export const BrandThemeProvider: React.FC<BrandThemeProviderProps> = ({
  brand,
  children,
}) => {
  const value = useMemo(
    () => ({
      brand,
      theme: brandThemes[brand],
    }),
    [brand],
  );

  return (
    <BrandThemeContext.Provider value={value}>
      {children}
    </BrandThemeContext.Provider>
  );
};

/**
 * Hook to access brand theme in V4 booking form components
 */
export const useBrandTheme = (): BrandThemeContextValue => {
  return useContext(BrandThemeContext);
};

/**
 * Utility to get CTA button classes based on brand and enabled state
 */
export const getCtaClasses = (theme: BrandTheme, isEnabled: boolean): string => {
  if (!isEnabled) {
    return 'bg-gray-100 text-gray-400 cursor-not-allowed';
  }
  return `${theme.ctaBg} text-white ${theme.ctaHover} shadow-mdy ${theme.ctaShadow} ${theme.ctaRing}`;
};

/**
 * Utility to get selected state classes
 */
export const getSelectedClasses = (theme: BrandTheme, isSelected: boolean): string => {
  if (!isSelected) {
    return 'border-gray-200 bg-white hover:border-gray-300';
  }
  return `${theme.accentBorder} ${theme.accentBgLight} shadow-md-tent`;
};

